import {on} from 'delegated-events'
import {PANEL_IDS} from './types'

type PrivateServerValidationStatus = {
  scheme: 'success' | 'warning'
  message: string
}

type PrivateServerFormState = {
  rootFields: Record<string, string>
}

type PrivateServerUrlFetchState = {
  state: 'idle' | 'loading' | 'success' | 'error'
  message?: string
}

type PrivateServerSchemaInput = HTMLInputElement | HTMLTextAreaElement

const privateServerUrlFetchControllers = new WeakMap<HTMLFormElement, AbortController>()
const privateServerUrlFetchState = new WeakMap<HTMLFormElement, PrivateServerUrlFetchState>()
const privateServerUrlInputTimers = new WeakMap<HTMLFormElement, ReturnType<typeof setTimeout>>()

function getPrivateServerFieldInputs(form: HTMLFormElement): {
  rootFieldInputs: PrivateServerSchemaInput[]
  rawJsonInput: HTMLTextAreaElement | null
  jsonUrlInput: HTMLInputElement | null
} {
  return {
    rootFieldInputs: Array.from(form.querySelectorAll<PrivateServerSchemaInput>('[data-private-server-root-field]')),
    rawJsonInput: form.querySelector<HTMLTextAreaElement>('textarea[name="private_server[raw_json]"]'),
    jsonUrlInput: form.querySelector<HTMLInputElement>('input[name="private_server[json_url]"]'),
  }
}

function getPrivateServerRootFieldName(input: PrivateServerSchemaInput): string | null {
  return input.getAttribute('data-private-server-root-field')
}

function getPrivateServerFormState(form: HTMLFormElement): PrivateServerFormState {
  const {rootFieldInputs} = getPrivateServerFieldInputs(form)
  const rootFields = rootFieldInputs.reduce<Record<string, string>>((values, input) => {
    const fieldName = getPrivateServerRootFieldName(input)
    if (!fieldName) return values

    values[fieldName] = input.value.trim()
    return values
  }, {})

  return {
    rootFields,
  }
}

function buildPrivateServerPayloadFromFormState(form: HTMLFormElement): Record<string, unknown> {
  const formState = getPrivateServerFormState(form)

  const payload = Object.entries(formState.rootFields).reduce<Record<string, unknown>>((result, [field, value]) => {
    if (value.length > 0) {
      result[field] = value
    }
    return result
  }, {})

  const serverName = formState.rootFields.name || ''

  // The first-pass form relies on root field inputs and defaults version so the
  // generated Raw JSON remains minimally valid when users only provide a name.
  if (serverName.length > 0 && typeof payload.version !== 'string') {
    payload.version = '1.0.0'
  }

  return payload
}

function writeRawJsonFromFormState(form: HTMLFormElement): void {
  const {rawJsonInput} = getPrivateServerFieldInputs(form)
  if (!rawJsonInput) return

  const payload = buildPrivateServerPayloadFromFormState(form)
  rawJsonInput.value = JSON.stringify(payload, null, 2)
}

function writeFormStateFromRawJson(form: HTMLFormElement): void {
  const {rootFieldInputs, rawJsonInput} = getPrivateServerFieldInputs(form)
  if (!rawJsonInput) return

  let parsed: unknown
  try {
    parsed = JSON.parse(rawJsonInput.value)
  } catch {
    return
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return

  const config = parsed as Record<string, unknown>

  for (const input of rootFieldInputs) {
    const fieldName = getPrivateServerRootFieldName(input)
    if (!fieldName) continue

    const rootValue = config[fieldName]
    input.value = typeof rootValue === 'string' ? rootValue : ''
  }
}

function getEmbeddedServerJsonFromUrl(urlValue: string): string | null {
  try {
    const parsedUrl = new URL(urlValue, window.location.origin)
    const embedded = parsedUrl.searchParams.get('server_json') || parsedUrl.searchParams.get('serverJson')
    return embedded?.trim() || null
  } catch {
    return null
  }
}

function getPrivateServerUrlPreviewElements(form: HTMLFormElement): {
  previewContainer: HTMLElement | null
  nameElement: HTMLElement | null
} {
  return {
    previewContainer: form.querySelector<HTMLElement>('[data-private-server-url-name-preview]'),
    nameElement: form.querySelector<HTMLElement>('[data-private-server-url-name]'),
  }
}

function setPrivateServerUrlNamePreview(form: HTMLFormElement, serverName: string | null): void {
  const {previewContainer, nameElement} = getPrivateServerUrlPreviewElements(form)
  if (!previewContainer || !nameElement) return

  if (serverName && serverName.trim().length > 0) {
    nameElement.textContent = serverName
    previewContainer.hidden = false
    return
  }

  nameElement.textContent = ''
  previewContainer.hidden = true
}

function clearPrivateServerJsonUrl(form: HTMLFormElement): void {
  const {jsonUrlInput} = getPrivateServerFieldInputs(form)
  if (jsonUrlInput && jsonUrlInput.value.length > 0) {
    jsonUrlInput.value = ''
    setPrivateServerUrlNamePreview(form, null)
    setPrivateServerUrlFetchState(form, {state: 'idle'})
    privateServerUrlFetchControllers.get(form)?.abort()
  }
}

function getEmbeddedServerObjectFromUrl(urlValue: string): Record<string, unknown> | null {
  const embedded = getEmbeddedServerJsonFromUrl(urlValue)
  if (!embedded) return null

  try {
    const parsed = JSON.parse(embedded)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    return parsed as Record<string, unknown>
  } catch {
    return null
  }
}

function getPrivateServerUrlFetchState(form: HTMLFormElement): PrivateServerUrlFetchState {
  return privateServerUrlFetchState.get(form) || {state: 'idle'}
}

function setPrivateServerUrlFetchState(form: HTMLFormElement, state: PrivateServerUrlFetchState): void {
  privateServerUrlFetchState.set(form, state)
}

function normalizePrivateServerPayloadFromFetchedJson(parsed: unknown): Record<string, unknown> | null {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null

  const topLevel = parsed as Record<string, unknown>
  const serverCandidate =
    topLevel.server && typeof topLevel.server === 'object' && !Array.isArray(topLevel.server)
      ? (topLevel.server as Record<string, unknown>)
      : topLevel

  const name = typeof serverCandidate.name === 'string' ? serverCandidate.name.trim() : ''
  const version = typeof serverCandidate.version === 'string' ? serverCandidate.version.trim() : ''
  const replacementLink =
    typeof serverCandidate.replacement_link === 'string' ? serverCandidate.replacement_link.trim() : ''

  if (name.length === 0) return null

  const payload: Record<string, unknown> = {
    name,
    version: version.length > 0 ? version : '1.0.0',
  }

  if (replacementLink.length > 0) {
    payload.replacement_link = replacementLink
  }

  return payload
}

async function fetchPrivateServerPayloadFromUrl(
  urlValue: string,
  signal: AbortSignal,
  form: HTMLFormElement,
): Promise<Record<string, unknown> | null> {
  const proxyPath = form.getAttribute('data-fetch-server-json-path')

  let fetchUrl: string
  let fetchInit: RequestInit

  if (proxyPath) {
    // Use server-side proxy to avoid CSP connect-src restrictions.
    fetchUrl = `${proxyPath}?url=${encodeURIComponent(urlValue)}`
    fetchInit = {method: 'GET', headers: {Accept: 'application/json'}, signal}
  } else {
    // Fallback to direct fetch (only works if CSP allows the target).
    fetchUrl = urlValue
    fetchInit = {method: 'GET', headers: {Accept: 'application/json'}, signal}
  }

  const response = await fetch(fetchUrl, fetchInit)

  if (!response.ok) {
    return null
  }

  const parsed = await response.json()
  return normalizePrivateServerPayloadFromFetchedJson(parsed)
}

function getPrivateServerUrlFetchFailureMessage(): string {
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  return 'Not ready: unable to load server JSON'
}

async function syncPrivateServerFromJsonUrl(form: HTMLFormElement): Promise<void> {
  const {rawJsonInput, jsonUrlInput} = getPrivateServerFieldInputs(form)
  if (!jsonUrlInput || !rawJsonInput) return

  const urlValue = jsonUrlInput.value.trim()
  if (urlValue.length === 0) {
    setPrivateServerUrlNamePreview(form, null)
    setPrivateServerUrlFetchState(form, {state: 'idle'})
    return
  }

  const urlValidationError = validatePrivateServerJsonUrl(urlValue)
  if (urlValidationError) {
    setPrivateServerUrlNamePreview(form, null)
    setPrivateServerUrlFetchState(form, {
      state: 'error',
      message: getFriendlyPrivateServerUrlValidationMessage(urlValidationError),
    })
    return
  }

  const embeddedServerObject = getEmbeddedServerObjectFromUrl(urlValue)
  const embeddedName = typeof embeddedServerObject?.name === 'string' ? embeddedServerObject.name : null
  setPrivateServerUrlNamePreview(form, embeddedName)

  const embeddedServerJson = getEmbeddedServerJsonFromUrl(urlValue)
  if (embeddedServerJson) {
    rawJsonInput.value = embeddedServerJson
    writeFormStateFromRawJson(form)
    setPrivateServerUrlFetchState(form, {state: 'success'})
    return
  }

  privateServerUrlFetchControllers.get(form)?.abort()
  const controller = new AbortController()
  privateServerUrlFetchControllers.set(form, controller)

  setPrivateServerUrlFetchState(form, {state: 'loading'})

  try {
    const payload = await fetchPrivateServerPayloadFromUrl(urlValue, controller.signal, form)
    if (!payload) {
      setPrivateServerUrlNamePreview(form, null)
      setPrivateServerUrlFetchState(form, {
        state: 'error',
        message: getPrivateServerUrlFetchFailureMessage(),
      })
      return
    }

    rawJsonInput.value = JSON.stringify(payload, null, 2)
    writeFormStateFromRawJson(form)

    const payloadName = typeof payload.name === 'string' ? payload.name : null
    setPrivateServerUrlNamePreview(form, payloadName)
    setPrivateServerUrlFetchState(form, {state: 'success'})
  } catch {
    if (controller.signal.aborted) return

    setPrivateServerUrlNamePreview(form, null)
    setPrivateServerUrlFetchState(form, {
      state: 'error',
      message: getPrivateServerUrlFetchFailureMessage(),
    })
  }
}

function getPrivateServerFooterStatusElement(form: Element): HTMLElement | null {
  const formId = form.getAttribute('id')
  if (!formId) return null

  return document.querySelector<HTMLElement>(
    `[data-private-server-validation-status][data-private-server-form-id="${formId}"]`,
  )
}

function setPrivateServerValidationStatus(form: Element, status: PrivateServerValidationStatus | null): void {
  const statusElement = getPrivateServerFooterStatusElement(form)
  if (!statusElement) return

  const successIcon = statusElement.querySelector<HTMLElement>('[data-private-server-validation-icon-success]')
  const warningIcon = statusElement.querySelector<HTMLElement>('[data-private-server-validation-icon-warning]')
  const textElement = statusElement.querySelector<HTMLElement>('[data-private-server-validation-text]')
  if (!textElement) return

  if (!status) {
    textElement.textContent = ''
    statusElement.hidden = true
    if (successIcon) successIcon.hidden = true
    if (warningIcon) warningIcon.hidden = true
    return
  }

  textElement.textContent = status.message
  statusElement.hidden = false
  if (successIcon) successIcon.hidden = status.scheme !== 'success'
  if (warningIcon) warningIcon.hidden = status.scheme !== 'warning'
}

function setPrivateServerJsonError(form: Element, message: string | null): void {
  const errorElement = form.querySelector<HTMLElement>('[data-private-server-json-error]')
  if (!errorElement) return

  if (message) {
    errorElement.textContent = message
    errorElement.hidden = false
    return
  }

  errorElement.textContent = ''
  errorElement.hidden = true
}

function setPrivateServerSubmitDisabled(form: HTMLFormElement, disabled: boolean): void {
  const formId = form.getAttribute('id')
  if (!formId) return

  const saveButton = document.querySelector<HTMLButtonElement>(
    `[data-private-server-save-button][data-private-server-form-id="${formId}"]`,
  )
  if (!saveButton) return

  saveButton.disabled = disabled
}

function validatePrivateServerRawJson(jsonValue: string): string | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(jsonValue)
  } catch {
    // eslint-disable-next-line i18n-text/no-en -- Validation message shown in internal admin dialog.
    return 'Raw JSON must be valid JSON.'
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    // eslint-disable-next-line i18n-text/no-en -- Validation message shown in internal admin dialog.
    return 'Raw JSON must be a JSON object.'
  }

  const config = parsed as Record<string, unknown>

  if (typeof config.name !== 'string' || config.name.trim().length === 0) {
    // eslint-disable-next-line i18n-text/no-en -- Validation message shown in internal admin dialog.
    return "Raw JSON field 'name' is required and must be a non-empty string."
  }
  if (typeof config.version !== 'string' || config.version.trim().length === 0) {
    // eslint-disable-next-line i18n-text/no-en -- Validation message shown in internal admin dialog.
    return "Raw JSON field 'version' is required and must be a non-empty string."
  }
  return null
}

function validatePrivateServerJsonUrl(
  urlValue: string,
): 'invalid_url' | 'https_required' | 'invalid_embedded_server_json' | null {
  if (!/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(urlValue)) {
    return 'invalid_url'
  }

  let parsedUrl: URL
  try {
    parsedUrl = new URL(urlValue, window.location.origin)
  } catch {
    return 'invalid_url'
  }

  if (parsedUrl.protocol !== 'https:') {
    return 'https_required'
  }

  const embeddedServerJson = getEmbeddedServerJsonFromUrl(urlValue)
  if (!embeddedServerJson) return null

  const embeddedValidationError = validatePrivateServerRawJson(embeddedServerJson)
  if (embeddedValidationError) {
    return 'invalid_embedded_server_json'
  }

  return null
}

function getFriendlyPrivateServerValidationMessage(message: string): string {
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  if (message.includes("field 'name'")) return 'Not ready: missing name'
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  if (message.includes("field 'version'")) return 'Not ready: missing version'
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  if (message.includes('must be valid JSON')) return 'Not ready: invalid JSON'
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  if (message.includes('must be a JSON object')) return 'Not ready: JSON must be an object'
  return message
}

function getFriendlyPrivateServerUrlValidationMessage(
  validationCode: 'invalid_url' | 'https_required' | 'invalid_embedded_server_json',
): string {
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  if (validationCode === 'invalid_url') return 'Not ready: invalid URL'
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  if (validationCode === 'https_required') return 'Not ready: URL must use HTTPS'
  // eslint-disable-next-line i18n-text/no-en -- Short internal status label in admin UI.
  return 'Not ready: invalid embedded server JSON'
}

function getPrivateServerValidationStatus(form: HTMLFormElement): PrivateServerValidationStatus | null {
  const dialogBody = form.closest('[data-view-component]')
  const inputMode =
    form.querySelector<HTMLInputElement>('.js-private-server-input-mode') ||
    dialogBody?.querySelector<HTMLInputElement>('.js-private-server-input-mode') ||
    null
  if (!inputMode) return null

  if (inputMode.value === 'form') {
    const formState = getPrivateServerFormState(form)
    const name = formState.rootFields.name || ''

    if (name.length === 0) {
      return {
        scheme: 'warning',
        message: 'Not ready: missing name',
      }
    }

    return {
      scheme: 'success',
      message: 'Ready to submit',
    }
  }

  if (inputMode.value === 'url') {
    const {jsonUrlInput} = getPrivateServerFieldInputs(form)
    if (!jsonUrlInput) return null

    const urlValue = jsonUrlInput.value.trim()
    if (urlValue.length === 0) {
      return {
        scheme: 'warning',
        message: 'Not ready: missing JSON URL',
      }
    }

    const currentUrlFetchState = getPrivateServerUrlFetchState(form)

    if (currentUrlFetchState.state === 'loading') {
      return {
        scheme: 'warning',
        message: 'Not ready: loading server JSON',
      }
    }

    if (currentUrlFetchState.state === 'error') {
      return {
        scheme: 'warning',
        message: currentUrlFetchState.message || getPrivateServerUrlFetchFailureMessage(),
      }
    }

    if (currentUrlFetchState.state !== 'success') {
      return {
        scheme: 'warning',
        message: getPrivateServerUrlFetchFailureMessage(),
      }
    }

    return {
      scheme: 'success',
      message: 'Ready to submit',
    }
  }

  if (inputMode.value !== 'json') return null

  const rawJsonInput = form.querySelector<HTMLTextAreaElement>('textarea[name="private_server[raw_json]"]')
  if (!rawJsonInput) return null

  if (rawJsonInput.value.trim().length === 0) {
    return {
      scheme: 'warning',
      message: 'Not ready: missing server JSON',
    }
  }

  const validationError = validatePrivateServerRawJson(rawJsonInput.value)
  if (validationError) {
    return {
      scheme: 'warning',
      message: getFriendlyPrivateServerValidationMessage(validationError),
    }
  }

  return {
    scheme: 'success',
    message: 'Ready to submit',
  }
}

function validatePrivateServerFormBeforeSubmit(form: HTMLFormElement): string | null {
  const validationStatus = getPrivateServerValidationStatus(form)
  if (!validationStatus || validationStatus.scheme === 'success') return null
  return validationStatus.message
}

function updatePrivateServerValidationUI(form: HTMLFormElement): void {
  const validationStatus = getPrivateServerValidationStatus(form)

  const dialogBody = form.closest('[data-view-component]')
  const inputMode =
    form.querySelector<HTMLInputElement>('.js-private-server-input-mode')?.value ||
    dialogBody?.querySelector<HTMLInputElement>('.js-private-server-input-mode')?.value ||
    null
  if (inputMode !== 'url') {
    setPrivateServerUrlNamePreview(form, null)
  }
  setPrivateServerValidationStatus(form, validationStatus)
  setPrivateServerJsonError(
    form,
    inputMode === 'json' && validationStatus?.scheme === 'warning' ? validationStatus.message : null,
  )
  setPrivateServerSubmitDisabled(form, validationStatus?.scheme === 'warning')
}

function attachPrivateServerRealtimeValidation(form: HTMLFormElement): void {
  if (form.getAttribute('data-private-server-realtime-validation-attached') === 'true') return

  const {rawJsonInput, rootFieldInputs, jsonUrlInput} = getPrivateServerFieldInputs(form)

  rawJsonInput?.addEventListener('input', () => {
    writeFormStateFromRawJson(form)
    updatePrivateServerValidationUI(form)
  })

  for (const input of rootFieldInputs) {
    input.addEventListener('input', () => {
      writeRawJsonFromFormState(form)
      updatePrivateServerValidationUI(form)
    })
  }

  jsonUrlInput?.addEventListener('input', () => {
    const existingTimer = privateServerUrlInputTimers.get(form)
    if (existingTimer) clearTimeout(existingTimer)

    const timer = setTimeout(() => {
      void (async () => {
        await syncPrivateServerFromJsonUrl(form)
        updatePrivateServerValidationUI(form)
      })()
    }, 400)
    privateServerUrlInputTimers.set(form, timer)
  })

  form.setAttribute('data-private-server-realtime-validation-attached', 'true')
}

function getPanelIdFromLink(link: HTMLAnchorElement): string | null {
  if (link.hash) {
    return link.hash.startsWith('#') ? link.hash.slice(1) : link.hash
  }

  const href = link.getAttribute('href')
  if (!href) return null
  return href.startsWith('#') ? href.slice(1) : href
}

function updateSegmentedControlSelectedState(container: Element | null, link: HTMLAnchorElement): void {
  const segmentedControl = container?.querySelector('segmented-control')
  if (!segmentedControl) return

  const allItems = segmentedControl.querySelectorAll('li.SegmentedControl-item')
  for (const item of allItems) {
    item.classList.remove('SegmentedControl-item--selected')
    item.querySelector('[aria-current]')?.removeAttribute('aria-current')
  }

  const clickedItem = link.closest('li.SegmentedControl-item')
  clickedItem?.classList.add('SegmentedControl-item--selected')
  link.setAttribute('aria-current', 'true')
}

// SegmentedControl toggle handler - registered at module load time
on('click', '.js-mcp-toggle-test a', function (event) {
  event.preventDefault()

  const link = event.currentTarget as HTMLAnchorElement
  const panelId = getPanelIdFromLink(link)
  if (!panelId) return

  // Find the container that holds both the toggle and panels
  const container = link.closest('.js-mcp-toggle-test')
  const dialogBody = container?.closest('[data-view-component]') || container?.parentElement

  // Private server form/json toggle
  if (container?.getAttribute('data-private-server-toggle') === 'true') {
    const formPanel = dialogBody?.querySelector<HTMLElement>('[data-private-server-panel="form"]')
    const jsonPanel = dialogBody?.querySelector<HTMLElement>('[data-private-server-panel="json"]')
    const urlPanel = dialogBody?.querySelector<HTMLElement>('[data-private-server-panel="url"]')
    const form = dialogBody?.querySelector<HTMLFormElement>('form[data-private-server-form="true"]')
    const inputMode =
      form?.querySelector<HTMLInputElement>('.js-private-server-input-mode') ||
      dialogBody?.querySelector<HTMLInputElement>('.js-private-server-input-mode') ||
      null

    if (formPanel && jsonPanel) {
      const showJson = panelId.endsWith('-panel-json')
      const showForm = panelId.endsWith('-panel-form')
      const showUrl = panelId.endsWith('-panel-url')
      if (showJson || showForm || showUrl) {
        formPanel.hidden = !showForm
        jsonPanel.hidden = !showJson
        if (urlPanel) urlPanel.hidden = !showUrl
        if (inputMode) {
          if (showJson) inputMode.value = 'json'
          if (showForm) inputMode.value = 'form'
          if (showUrl) inputMode.value = 'url'
        }

        if (form) {
          if (showJson) {
            writeRawJsonFromFormState(form)
          }

          if (showUrl) {
            void (async () => {
              await syncPrivateServerFromJsonUrl(form)
              updatePrivateServerValidationUI(form)
            })()
          }

          updatePrivateServerValidationUI(form)
        }
      }
    }

    updateSegmentedControlSelectedState(container, link)
    return
  }

  // Toggle panels - search within the dialog context
  const externalPanel = dialogBody?.querySelector<HTMLElement>(`#${PANEL_IDS.EXTERNAL}`)
  const githubPanel = dialogBody?.querySelector<HTMLElement>(`#${PANEL_IDS.GITHUB}`)

  if (externalPanel) externalPanel.hidden = panelId !== PANEL_IDS.EXTERNAL
  if (githubPanel) githubPanel.hidden = panelId !== PANEL_IDS.GITHUB

  updateSegmentedControlSelectedState(container, link)

  // Update hidden input - only set for known panel IDs
  const input = container?.querySelector<HTMLInputElement>('.js-mcp-type-value')
  if (input) {
    if (panelId === PANEL_IDS.EXTERNAL) {
      input.value = 'external'
    } else if (panelId === PANEL_IDS.GITHUB) {
      input.value = 'github'
    }
  }

  // Toggle registry URL fields so only the active panel's URL submits
  const externalUrl = dialogBody?.querySelector<HTMLInputElement>('input.js-mcp-external-url')
  const githubUrl = dialogBody?.querySelector<HTMLInputElement>('input.js-mcp-github-url')
  if (externalUrl instanceof HTMLInputElement) externalUrl.disabled = panelId !== PANEL_IDS.EXTERNAL
  if (githubUrl instanceof HTMLInputElement) githubUrl.disabled = panelId !== PANEL_IDS.GITHUB
})

on('submit', 'form[data-private-server-form="true"]', function (event) {
  const form = event.currentTarget as HTMLFormElement
  updatePrivateServerValidationUI(form)
  event.preventDefault()

  const errorMessage = validatePrivateServerFormBeforeSubmit(form)

  if (errorMessage) {
    event.preventDefault()
    setPrivateServerJsonError(form, errorMessage)
    return
  }

  setPrivateServerJsonError(form, null)
})

on('change', 'form[data-private-server-form="true"] textarea[name="private_server[raw_json]"]', function (event) {
  const textarea = event.currentTarget as HTMLTextAreaElement
  const form = textarea.closest('form[data-private-server-form="true"]')
  if (!(form instanceof HTMLFormElement)) return

  clearPrivateServerJsonUrl(form)
  writeFormStateFromRawJson(form)
  updatePrivateServerValidationUI(form)
})

on('change', 'form[data-private-server-form="true"] input[name="private_server[json_url]"]', function (event) {
  const input = event.currentTarget as HTMLInputElement
  const form = input.closest('form[data-private-server-form="true"]')
  if (!(form instanceof HTMLFormElement)) return

  void (async () => {
    await syncPrivateServerFromJsonUrl(form)
    updatePrivateServerValidationUI(form)
  })()
})

on('change', 'form[data-private-server-form="true"] [data-private-server-root-field]', function (event) {
  const input = event.currentTarget as HTMLElement
  const form = input.closest('form[data-private-server-form="true"]')
  if (!(form instanceof HTMLFormElement)) return

  clearPrivateServerJsonUrl(form)
  writeRawJsonFromFormState(form)
  updatePrivateServerValidationUI(form)
})

for (const form of document.querySelectorAll<HTMLFormElement>('form[data-private-server-form="true"]')) {
  attachPrivateServerRealtimeValidation(form)
  const inputMode = form.querySelector<HTMLInputElement>('.js-private-server-input-mode')?.value
  if (inputMode === 'url') {
    void (async () => {
      await syncPrivateServerFromJsonUrl(form)
      updatePrivateServerValidationUI(form)
    })()
    continue
  }
  updatePrivateServerValidationUI(form)
}
